type AllocationPoint = [number, number | null]
type Interval = [number, number]

export const MIN_VISIBLE_ALLOCATION = 0.01
/** Share of the stacked total below which a band is too thin to need its own color. */
const MIN_VISIBLE_SHARE = 0.01

interface AllocationPalette {
  colors: readonly string[]
  areaChartColors: readonly string[]
}

function getStackTotals(series: readonly { data: AllocationPoint[] }[]): Map<number, number> {
  const totals = new Map<number, number>()
  for (const { data } of series) {
    for (const [x, y] of data) {
      if (y === null || !Number.isFinite(y) || y <= 0) continue
      totals.set(x, (totals.get(x) ?? 0) + y)
    }
  }
  return totals
}

// Visibility is relative to the stacked total at each sample, so token units
// (WBTC vs USDC) do not change which bands count as overlapping.
function getActiveIntervals(data: AllocationPoint[], totals: Map<number, number>): Interval[] {
  const intervals: Interval[] = []
  let previous: { x: number; visible: boolean } | undefined

  for (const point of data) {
    const [x, y] = point
    // Allocation charts connect nulls, so use the preceding non-null sample.
    if (y === null || !Number.isFinite(y)) continue
    const total = totals.get(x) ?? 0
    const visible = y > 0 && total > 0 && y / total >= MIN_VISIBLE_SHARE
    const start = previous && (previous.visible || visible) ? previous.x : x
    if (visible || previous?.visible) {
      // Include isolated visible samples in the overlap comparison (x is in ms).
      const end = visible ? x + 1 : x
      const last = intervals.at(-1)
      if (last && last[1] >= start) last[1] = Math.max(last[1], end)
      else intervals.push([start, end])
    }
    previous = { x, visible }
  }

  return intervals
}

function overlapDuration(a: Interval[], b: Interval[]): number {
  let i = 0
  let j = 0
  let duration = 0
  while (i < a.length && j < b.length) {
    const left = a[i]
    const right = b[j]
    if (!left || !right) break
    duration += Math.max(0, Math.min(left[1], right[1]) - Math.max(left[0], right[0]))
    if (left[1] < right[1]) i++
    else j++
  }
  return duration
}

/** Assign colors once per dataset, independently of hover, units, and theme. */
export function colorAllocationSeries<T extends { data: AllocationPoint[] }>(
  series: T[],
  palette: AllocationPalette
) {
  if (palette.colors.length === 0) throw new Error('Allocation palette must not be empty')

  interface Node {
    item: T
    intervals: Interval[]
    neighbors: { other: Node; duration: number }[]
    /** Distinct colors already taken by this node's neighbors. */
    saturation: Set<number>
    colorIndex: number
  }

  const withData = series.filter(item =>
    item.data.some(([, y]) => y !== null && Number.isFinite(y) && y > 0)
  )
  const totals = getStackTotals(withData)
  const nodes: Node[] = withData.map(item => ({
    item,
    intervals: getActiveIntervals(item.data, totals),
    neighbors: [],
    saturation: new Set(),
    colorIndex: -1
  }))
  // Overlapping series, computed once per series pair.
  for (const a of nodes) {
    for (const b of nodes) {
      if (a === b) continue
      const duration = overlapDuration(a.intervals, b.intervals)
      if (duration > 0) a.neighbors.push({ other: b, duration })
    }
  }
  const uses = palette.colors.map((_, colorIndex) => ({ colorIndex, count: 0 }))
  const pending = new Set(nodes)

  // Color the most constrained series first. Ties keep the source order; the
  // returned series never move, so the chart's stack order remains unchanged.
  while (pending.size > 0) {
    let next: Node | undefined
    for (const node of pending) {
      if (
        !next ||
        node.saturation.size > next.saturation.size ||
        (node.saturation.size === next.saturation.size &&
          node.neighbors.length > next.neighbors.length)
      ) {
        next = node
      }
    }
    if (!next) break

    const ranked = uses.map(({ colorIndex, count }) => ({
      colorIndex,
      conflicts: 0,
      duration: 0,
      uses: count
    }))
    for (const { other, duration } of next.neighbors) {
      const taken = ranked[other.colorIndex]
      if (!taken) continue
      taken.conflicts++
      taken.duration += duration
    }
    ranked.sort(
      (a, b) =>
        a.conflicts - b.conflicts ||
        a.duration - b.duration ||
        a.uses - b.uses ||
        a.colorIndex - b.colorIndex
    )
    const best = ranked[0]
    if (!best) break
    next.colorIndex = best.colorIndex
    const use = uses[best.colorIndex]
    if (use) use.count++
    pending.delete(next)
    for (const { other } of next.neighbors) other.saturation.add(best.colorIndex)
  }

  return nodes.map(({ item, colorIndex }) => ({
    ...item,
    colorIndex,
    color: palette.colors[colorIndex],
    lineColor: palette.colors[colorIndex],
    fillColor: palette.areaChartColors[colorIndex]
  }))
}
